<template>

    <div id="jobtoolz-application" class="min-w-1024">
        <div v-if="loggingOut">
            <!-- show nothing whilst we're reloading on logout -->
        </div>
        <div v-else>
            <navigation :disabled="disabledComponents"></navigation>

            <div ref="container"
                 class="pl-64 bg-primary-5"
            >
                <div class="h-screen flex flex-col relative">

                    <top-menu :disabled="disabledComponents" class="z-80"></top-menu>

                    <template v-if="licenseDaysLeft !== null && licenseDaysLeft !== false && $can('settings.finance') && companyActive">
                        <banner
                            v-if="showLicenseBanner"
                            :content="getLicenseBannerContent"
                            :display="getLicenseBannerDisplay"
                        />
                    </template>

                    <banner
                        v-if="daysUntilCompanyDisabled !== false && companyActive && showInvoiceAlert"
                        :description="$t('companypage.invoice_reminder.title.' + invoiceReminderCount, {count: daysUntilCompanyDisabled})"
                        danger
                    />

                    <popup-modal-confirm
                        v-if="unsafePassword"
                        :confirmText="$t('auth.change_my_password')"
                        :message="$t('auth.password_leaked.description')"
                        :title="$t('auth.password_leaked.title')"
                        confirm-button="success"
                        show-exclamation
                        @close="unsafePassword = 0"
                        @confirm="goToUserPage"
                    />

                    <popup-modal-confirm
                        v-if="mainStore.wrongRevision"
                        :confirmText="$t('general.wrong_version.button')"
                        :message="$t('general.wrong_version.description')"
                        :show-cancel-button="false"
                        :title="$t('general.wrong_version.title')"
                        :working="reloading"
                        confirm-button="success"
                        confirm-icon="refresh"
                        @confirm="reloadPage"
                    />

                    <popup-modal
                        v-if="companyActive && showBoosterPopup"
                        position="center"
                        container-classes="w-full"
                        custom-classes="w-2/4"
                        @close="showBoosterPopup = false"
                    >
                        <div class="w-full flex items-center gap-24">
                            <div class="flex flex-col w-1/2">
                                <!-- Title block -->
                                <div class="mb-32">
                                    <h2 class="font-semibold text-heading-2">{{ $t('social_boost.popup.title') }}</h2>
                                    <p class="text-body">{{ $t('social_boost.popup.description') }}</p>
                                </div>
                                <!-- Form block -->
                                <div class="flex flex-col gap-16">
                                    <div class="flex flex-col">
                                        <label for="vacancy" class="font-semibold text-body mb-8">{{ $t('social_boost.popup.vacancy.title') }}</label>
                                        <select-dropdown
                                            v-model="selectedBoosterVacancyId"
                                            :loading="boosterVacanciesLoading"
                                            ref="boosterVacancy"
                                            :auto-translate="false"
                                            :options="boosterVacancies"
                                            :placeholder="$t('social_boost.popup.vacancy.placeholder')"
                                            :allow-enter="true"
                                            @change="changeBoosterVacancy"
                                            searchable
                                        />
                                    </div>
                                    <div class="flex flex-col">
                                        <label for="boosters" class="font-semibold text-body mb-8">{{ $t('social_boost.popup.boost.title') }}</label>
                                        <select-dropdown
                                            v-model="selectedBoosterId"
                                            :loading="boostersLoading"
                                            ref="boosters"
                                            :auto-translate="false"
                                            :options="boosterOptions"
                                            :placeholder="$t('social_boost.popup.boost.placeholder')"
                                            :allow-enter="true"
                                            @change="changeBooster"
                                        />
                                    </div>
                                </div>
                                <!-- Button block -->
                                <div class="w-full">
                                    <simple-button
                                        class="mt-32 w-full"
                                        display="primary"
                                        :loading="savingBooster"
                                        :disabled="(boostersLoading || boosterVacanciesLoading) || (!selectedBooster || !selectedBoosterVacancy)"
                                        override-disabled-class="disabled:bg-primary-40 disabled:text-primary-60"
                                        @click="handleBoosterSubmit"
                                    >
                                        {{ $t('social_boost.popup.button')}}
                                    </simple-button>
                                </div>
                            </div>
                            <booster-card
                                v-if="selectedBooster"
                                :booster="selectedBooster"
                                class="w-1/2"
                            />
                            <div
                                v-else
                                class="w-1/2 flex flex-col items-center justify-center content-stretch"
                            >
                                <div
                                    class="grow w-full"
                                >
                                    <img
                                        v-if="!boostersLoading && boosterImageUrl"
                                        :src="boosterImageUrl"
                                        alt="booster-image"
                                        class="object-contain w-full h-full"
                                    >
                                </div>
                            </div>
                        </div>
                    </popup-modal>

                    <application-alerts/>

                    <div id="content-container"
                         :key="authenticationStore.companySwitches"
                         ref="scrollContainer"
                         :class="[ shouldScroll ? 'overflow-y-scroll' : 'overflow-y-hidden', noTopPadding ? '' : 'pt-32', noBottomPadding ? '' : 'pb-32' ]"
                         class="px-32 grow"
                    >
                        <router-view
                            v-if="companyActive || ['settings.pricing-plans', 'settings.billing-details', 'settings.invoices'].includes($route.name)"
                        />
                        <company-disabled v-else/>
                    </div>

                    <button type="button" id="toggle_cookie_popup" class="hidden absolute bottom-0 left-0 m-16 p-8 w-48 aspect-square z-top bg-green-dark/85 rounded-full hover:scale-105 hover:bg-green-dark transition-all">
                        <svg class="w-full h-full fill-white" viewBox="0 0 18 18">
                            <mask id="mask_cookie" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="180" height="180">
                            <rect width="180" height="180"/>
                            </mask>
                            <g mask="url(#mask_cookie)">
                            <path d="M9 16.5C7.9625 16.5 6.9875 16.3031 6.075 15.9094C5.1625 15.5156 4.36875 14.9812 3.69375 14.3062C3.01875 13.6312 2.48438 12.8375 2.09063 11.925C1.69687 11.0125 1.5 10.0375 1.5 8.99999C1.5 7.98749 1.70938 7.00312 2.12813 6.04687C2.54688 5.09062 3.12813 4.24999 3.87188 3.52499C4.61563 2.79999 5.5 2.24999 6.525 1.87499C7.55 1.49999 8.675 1.38749 9.9 1.53749C10.0875 1.56249 10.2313 1.64062 10.3313 1.77187C10.4313 1.90312 10.4875 2.08124 10.5 2.30624C10.525 3.10624 10.8219 3.79062 11.3906 4.35937C11.9594 4.92812 12.6375 5.22499 13.425 5.24999C13.6875 5.26249 13.8875 5.33749 14.025 5.47499C14.1625 5.61249 14.2375 5.82499 14.25 6.11249C14.275 6.63749 14.4344 7.06874 14.7281 7.40624C15.0219 7.74374 15.4312 7.99999 15.9562 8.17499C16.1312 8.23749 16.2656 8.32812 16.3594 8.44687C16.4531 8.56562 16.5 8.71249 16.5 8.88749C16.525 9.92499 16.3438 10.9062 15.9562 11.8312C15.5687 12.7562 15.0375 13.5656 14.3625 14.2594C13.6875 14.9531 12.8875 15.5 11.9625 15.9C11.0375 16.3 10.05 16.5 9 16.5ZM9 15C10.525 15 11.8781 14.475 13.0594 13.425C14.2406 12.375 14.8875 11.0375 15 9.41249C14.375 9.13749 13.8844 8.76249 13.5281 8.28749C13.1719 7.81249 12.9312 7.28124 12.8062 6.69374C11.8438 6.55624 11.0188 6.14374 10.3313 5.45624C9.64375 4.76874 9.21875 3.94374 9.05625 2.98124C8.05625 2.95624 7.17812 3.13749 6.42188 3.52499C5.66563 3.91249 5.03437 4.40937 4.52812 5.01562C4.02187 5.62187 3.64062 6.28124 3.38438 6.99374C3.12813 7.70624 3 8.37499 3 8.99999C3 10.6625 3.58438 12.0781 4.75313 13.2469C5.92188 14.4156 7.3375 15 9 15ZM7.875 7.49999C8.1875 7.49999 8.45312 7.39062 8.67188 7.17187C8.89062 6.95312 9 6.68749 9 6.37499C9 6.06249 8.89062 5.79687 8.67188 5.57812C8.45312 5.35937 8.1875 5.24999 7.875 5.24999C7.5625 5.24999 7.29688 5.35937 7.07812 5.57812C6.85938 5.79687 6.75 6.06249 6.75 6.37499C6.75 6.68749 6.85938 6.95312 7.07812 7.17187C7.29688 7.39062 7.5625 7.49999 7.875 7.49999ZM6.375 11.25C6.6875 11.25 6.95312 11.1406 7.17188 10.9219C7.39062 10.7031 7.5 10.4375 7.5 10.125C7.5 9.81249 7.39062 9.54687 7.17188 9.32812C6.95312 9.10937 6.6875 8.99999 6.375 8.99999C6.0625 8.99999 5.79688 9.10937 5.57812 9.32812C5.35938 9.54687 5.25 9.81249 5.25 10.125C5.25 10.4375 5.35938 10.7031 5.57812 10.9219C5.79688 11.1406 6.0625 11.25 6.375 11.25ZM11.25 12C11.4625 12 11.6406 11.9281 11.7844 11.7844C11.9281 11.6406 12 11.4625 12 11.25C12 11.0375 11.9281 10.8594 11.7844 10.7156C11.6406 10.5719 11.4625 10.5 11.25 10.5C11.0375 10.5 10.8594 10.5719 10.7156 10.7156C10.5719 10.8594 10.5 11.0375 10.5 11.25C10.5 11.4625 10.5719 11.6406 10.7156 11.7844C10.8594 11.9281 11.0375 12 11.25 12Z"/>
                            </g>
                        </svg>
                    </button>
                </div>
            </div>

            <task-tools/>
        </div>
    </div>

</template>

<script>
import Navigation from '../components/navigation/Navigation.vue';
import TopMenu from '../components/navigation/TopMenu.vue';
import ProfileEditor from '../components/ProfileEditor.vue';
import moment from 'moment';
import TaskTools from '../components/ui/TaskTools.vue';
import CompanyDisabled from '../components/CompanyDisabled';
import ApplicationAlerts from '../components/ApplicationAlerts.vue';
import _once from 'lodash/once';
import BillingDetails from '../models/BillingDetails';
import * as jobs from '../services/JobService';
import EventBus from '../../shared/eventbus';
import Invoice from '../models/Invoice';
import SimpleButton from '../components/ui/SimpleButton';
import Loading from '../components/ui/Loading.vue';
import PopupModalConfirm from '../components/ui/PopupModalConfirm.vue';
import PopupModal from "../components/ui/PopupModal.vue";
import DropdownButton from "../components/ui/DropdownButton.vue";
import SelectDropdown from "../components/forms/SelectDropdown.vue";
import Job from "../models/Job";
import * as JobService from "../services/JobService";
import Booster from "../models/Booster";
import * as BoosterService from "../services/BoosterService";
import {normalizeJson} from "../../shared/helpers";
import Price from "../components/Price.vue";
import SvgIcon from "../components/ui/SvgIcon.vue";
import BoosterCard from "../components/jobs/BoosterCard.vue";
import Banner from "../components/ui/Banner.vue";
import {mapActions, mapStores} from "pinia";
import {useMainStore} from "../state/store";
import {useRepo} from "pinia-orm";
import {useAuthenticationStore} from "../state/modules/authentication";
import {ADD_NOTIFICATION} from "../state/mutation-types";
import {useNotificationsStore} from "../state/modules/notifications";

export default {
    components: {
        TopMenu,
        Banner,
        BoosterCard,
        Price,
        SelectDropdown,
        DropdownButton,
        PopupModal,
        PopupModalConfirm,
        Loading,
        SimpleButton,
        CompanyDisabled,
        SvgIcon,
        'navigation': Navigation,
        'top-menu': TopMenu,
        'profile-editor': ProfileEditor,
        'task-tools': TaskTools,
        'application-alerts': ApplicationAlerts,
    },
    head() {
        return {
            title: 'Jobtoolz',
        }
    },
    data: function () {
        return {
            scrollPositions: {},
            ignoreScroll: [
                'job',
                'settings.email-templates',
                'settings.interview-templates',
                'settings.locations',
                'settings.users',
                'settings.roles',
                'settings.documents',
                'settings.languages',
                'settings.company',
                'settings.custom-css',
                'settings.tracking',
                'settings.domain-setup',
                'settings.pricing-plans',
                'settings.billing-details',
                'settings.invoices',
                'settings.api-keys',
                'settings.integrations',
                'settings.integrations.vdab',
                'settings.integrations.officient',
                'settings.privacy',
                'settings.metadata',
                'settings.favicon',
                'settings.labels',
                'settings.tags',
                'settings.competences',
                'settings.questions',
                'settings.reject-reasons',
            ],
            jobLimits: null,
            unsafePassword: this.currentUser?.unsafe_password,
            reloading: false,
            showBoosterPopup: false,
            selectedBoosterVacancyId: null,
            selectedBoosterVacancy: null,
            boosterVacanciesLoading: true,
            selectedBoosterId: null,
            selectedBooster: null,
            boostersLoading: true,
            boosterImageInterval: null,
            boosterImageUrl: null,
            savingBooster: false,
        };
    },
    computed: {
        ...mapStores(useMainStore, useAuthenticationStore),
        loggingOut() {
            return this.authenticationStore.loggingOut;
        },
        currentUser() {
            return this.mainStore.currentUser;
        },
        shouldScroll() {
            let routeName = this.$route.name?.split('.')[0];
            return !['job', 'settings', 'application'].includes(routeName);
        },
        noTopPadding() {
            return ['analytics'].includes(this.$route.name);
        },
        noBottomPadding() {
            return ['job'].includes(this.$route.name);
        },
        companyActive() {
            return this.mainStore.currentCompany && this.mainStore.currentCompany.active;
        },
        showInvoiceAlert() {
            if (this.mainStore.currentCompany.disable_invoice_alerts) {
                switch (this.mainStore.currentCompany.disable_invoice_alerts) {
                    case 'finance':
                        return this.$can('settings.finance');
                    case 'all':
                    default:
                        return false;
                }
            } else {
                return true;
            }
        },
        licenseDaysLeft() {
            if (!this.mainStore.currentCompany.license_expires_at) {
                return false;
            }

            const end = moment(this.mainStore.currentCompany.license_expires_at).endOf('day');

            return end.diff(moment().endOf('day'), 'days');
        },
        billingDetails() {
            return useRepo(BillingDetails).where('company_id', this.mainStore.currentCompany.id).first();
        },
        maxDaysLeft() {
            return this.billingDetails?.mandate_signed ? 7 : 14;
        },
        licenseDayText() {
            if ((this.licenseDaysLeft !== undefined) && this.licenseDaysLeft <= 14) {
                if (this.licenseDaysLeft === 0) {
                    return this.$t('companypage.license_expires.today');
                }
                if (this.licenseDaysLeft === 1) {
                    return this.$t('companypage.license_expires.tomorrow');
                }
                if (this.licenseDaysLeft < 0) {
                    return this.$t('companypage.license_expires.past');
                }

                return this.$t('companypage.license_expires.later', {days: this.licenseDaysLeft});
            }
        },
        downgradeCount() {
            if (!this.jobLimits || !this.jobLimits.downgrade_to) {
                return;
            }

            const isOverDowngradedLimit = this.jobLimits.downgrade_to < this.jobLimits.count;
            if (!isOverDowngradedLimit) {
                return;
            }

            return this.jobLimits.count - this.jobLimits.downgrade_to;
        },
        daysUntilCompanyDisabled() {
            const invoice = useRepo(Invoice)
                .where('company_id', this.authenticationStore.currentCompanyId)
                .where(val => ['BOOKED', 'PENDING', 'EXPIRED'].includes(val.state))
                .orderBy('due_date', 'asc')
                .first();

            if (!invoice) {
                return false;
            }

            const expiryDate = moment(invoice.due_date).endOf('day').add('2', 'weeks');
            const diffInDays = expiryDate.diff(moment().endOf('day'), 'days');

            //Only show this reminder to a non admin user when there are less than 2 days left
            if(diffInDays > 14 || !this.$can('settings.finance') && diffInDays > 2){
                return false;
            }

            return diffInDays;
        },
        invoiceReminderCount() {
            if (this.daysUntilCompanyDisabled <= 0) {
                return 0;
            } else if (this.daysUntilCompanyDisabled === 1) {
                return 1;
            } else if (this.daysUntilCompanyDisabled <= 3) {
                return 2;
            } else if (this.daysUntilCompanyDisabled <= 7) {
                return 4;
            } else {
                return 8;
            }
        },
        disabledComponents() {
            return this.$route.name === 'free-trial' ||
                this.$route.name === 'two-factor-required';
        },
        boosterVacancies() {
            this.boosterVacanciesLoading = true;
            let jobs = useRepo(Job).get().map(job => {
                let title;
                if (job.title[this.selectedLocale]) {
                    title = job.title[this.selectedLocale]
                } else {
                    // fallback to first available locale where title is not empty or null
                    for (let locale in job.title) {
                        if (job.title[locale] && job.title[locale] !== '') {
                            title = job.title[locale] + ' (' + locale + ')';
                            break;
                        }
                    }
                }
                return {
                    value: job.id,
                    label: title,
                    is_spontaneous: job.is_spontaneous,
                };
            }).sort((a, b) => {
                return a.label.localeCompare(b.label);
            }).sort((a, b) => {
                return b.is_spontaneous - a.is_spontaneous;
            });

            this.boosterVacanciesLoading = false;
            return jobs;
        },
        boosterOptions() {
            this.boostersLoading = true;
            let boosters = useRepo(Booster).where('active', true).orderBy('sequence', 'asc').get().map(booster => {
                let title;
                if (booster.title[this.selectedLocale]) {
                    title = booster.title[this.selectedLocale]
                } else {
                    // fallback to first available locale where title is not empty or null
                    for (let locale in booster.title) {
                        if (booster.title[locale] && booster.title[locale] !== '') {
                            title = booster.title[locale] + ' (' + locale + ')';
                            break;
                        }
                    }
                }
                return {
                    value: booster.id,
                    label: title,
                };
            });
            this.boostersLoading = false;
            return boosters;
        },
        showLicenseBanner() {
            return (this.licenseDaysLeft <= this.maxDaysLeft) ||
                (this.licenseDaysLeft <= 30 && this.downgradeCount) ||
                (this.licenseDaysLeft <= 30
                    && this.licenseDaysLeft > 15
                    && !this.mainStore.currentCompany.cancel_license
                )
        },
        getLicenseBannerContent() {
            if (this.licenseDaysLeft <= this.maxDaysLeft) {
                return {
                    title: this.$t('companypage.license_expires.title'),
                    body: this.licenseDayText,
                    link: null,
                }
            } else if (this.licenseDaysLeft <= 30 && this.downgradeCount) {
                return {
                    title: this.$t(
                        'companypage.downgrade_cancel.title',
                        { days: this.licenseDaysLeft - 14, count: this.downgradeCount }
                    ),
                    body: null,
                    link: null,
                }
            } else if (
                this.licenseDaysLeft <= 30
                && this.licenseDaysLeft > 15
                && !this.mainStore.currentCompany.cancel_license
            ) {
                return {
                    title: this.$t('companypage.reminder.title', { days: this.licenseDaysLeft - 14 }),
                    body: null,
                    link: null,
                }
            }
        },
        getLicenseBannerDisplay() {
            if (this.licenseDaysLeft <= this.maxDaysLeft) {
                return 'danger';
            } else if (this.licenseDaysLeft <= 30 && this.downgradeCount) {
                return 'warning';
            } else if (
                this.licenseDaysLeft <= 30
                && this.licenseDaysLeft > 15
                && !this.mainStore.currentCompany.cancel_license
            ) {
                return 'info';
            }
        }
    },
    methods: {
        ...mapActions(useMainStore, ['setScrollbarWidth', 'resetSelectedLocale']),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        initializeHubspotChat: _once((scrollbarWidth) => {
            let css = `#hubspot-messages-iframe-container { margin-right: ${scrollbarWidth}px; }`;
            let styles = document.createElement('style');
            styles.type = 'text/css';
            styles.appendChild(document.createTextNode(css));
            document.getElementsByTagName('head')[0].appendChild(styles);
            axios.get(route('app.hubspot')).then(response => {
                //Load hubspot
                window.hsConversationsSettings = response.data;
                window.HubSpotConversations.widget.load();

                //Open hubspot with parameter
                const params = new URLSearchParams(window.location.search);
                if (params.has('chat') && params.get('chat') == true) {
                    window.HubSpotConversations.widget.open();
                }
            });
        }),
        fetchLimits() {
            jobs.limits().then(data => {
                this.jobLimits = data;
            });
        },
        goToUserPage() {
            this.unsafePassword = 0;

            this.$router.push({name: 'user'});
        },
        reloadPage() {
            this.reloading = true;
            window.location.reload();
        },
        checkUrlForBoosterPopup() {
            // check for utm tags in url
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('utm_campaign') && urlParams.get('utm_campaign') === 'social-boost') {
                this.boostersLoading = true;
                this.boosterVacanciesLoading = true;
                this.showBoosterPopup = true;
            }

            if (this.showBoosterPopup) {
                this.fetchVacancies();
                this.fetchBoosters();
            }
        },
        fetchVacancies() {
            this.boosterVacanciesLoading = true;
            if (!useRepo(Job).get().length) {
                JobService.index({
                    filters: {
                        active: true,
                        published: true,
                    },
                    all: 1,
                }).then((result) => {
                    useRepo(Job).save(normalizeJson(result.data))
                    this.boosterVacanciesLoading = false;
                });
            } else {
                this.boosterVacanciesLoading = false;
            }
        },
        async fetchBoosters() {
            this.boostersLoading = true;
            if (!useRepo(Booster).first()) {
                await BoosterService.index().then(({data}) => {
                    useRepo(Booster).save(normalizeJson(data))
                    this.boostersLoading = false;
                    this.setBoosterImage();
                });
            } else {
                this.boostersLoading = false;
            }
        },
        changeBoosterVacancy(vacancyId) {
            this.selectedBoosterVacancy = useRepo(Job).find(vacancyId);
        },
        changeBooster(boosterId) {
            this.selectedBooster = useRepo(Booster).find(boosterId);
            this.setBoosterImage();
        },
        async handleBoosterSubmit() {
            if (this.selectedBoosterVacancy && this.selectedBooster) {
                this.savingBooster = true;
                await Booster.createBoosterCampaign(this.currentUser.id, {
                    data: {
                      job_id: this.selectedBoosterVacancy.id,
                      booster_id: this.selectedBooster.id,
                    }
                }).then((res) => {
                    this.savingBooster = false;
                    this.showBoosterPopup = false;
                    this.$router.push({name: 'campaign', params: {id: res.data.id}});
                }).catch(() => {
                    this.savingBooster = false;
                });
            }
        },
        setBoosterImage() {
            if (!this.boostersLoading && useRepo(Booster).first()) {
                if (!this.selectedBooster) {
                    if (!this.boosterImageInterval) {
                        this.boosterImageInterval = setInterval(() => {
                            this.setBoosterImage();
                        }, 5000);
                    }
                    let boosters = useRepo(Booster).where('active', true).get();
                    if (this.boosterImageUrl) {
                        boosters = boosters.filter(booster => booster.icon !== this.boosterImageUrl);
                    }
                    this.boosterImageUrl = boosters[Math.floor(Math.random() * boosters.length)].icon;
                } else {
                    if (this.boosterImageInterval) {
                        clearInterval(this.boosterImageInterval);
                    }
                    this.boosterImageUrl = this.selectedBooster.icon;
                }
            }
        },
    },
    mounted() {
        let container = this.$refs.scrollContainer;
        let scrollbarWidth = container.offsetWidth - container.clientWidth;

        this.mainStore.setScrollbarWidth(container.offsetWidth - container.clientWidth);

        let user = this.currentUser;
        let company = this.mainStore.currentCompany;

        this.$i18n.locale = user.language;

        if (Jobtoolz.environment == 'production') {
            if (window.HubSpotConversations) {
                this.initializeHubspotChat(scrollbarWidth);
            } else {
                window.hsConversationsOnReady = [
                    () => {
                        this.initializeHubspotChat(scrollbarWidth);
                    },
                ];
            }
        }

        this.$router.afterEach((to, from) => {
            let contentContainer = document.getElementById('content-container');

            this.mainStore.resetSelectedLocale();

            if (Jobtoolz.environment == 'production') {
                window.HubSpotConversations.widget.refresh();
            }

            if (!this.ignoreScroll.includes(from.name) && contentContainer) {
                this.$set(this.scrollPositions, from.name, contentContainer.scrollTop);
            }

            let scroll = 0;

            if (!this.ignoreScroll.includes(to.name) && this.scrollPositions.hasOwnProperty(to.name)) {
                scroll = this.scrollPositions[to.name];
            }

            this.$nextTick(() => {
                if (contentContainer) {
                    contentContainer.scrollTop = scroll;
                }
            });
        });

        this.fetchLimits();

        EventBus.addEventListener('fetch-job-limits', this.fetchLimits);

        this.checkUrlForBoosterPopup();

        // start alpine here to ensure cookie button is setup correctly
        if (window.Alpine) {
            window.Alpine.start();
        }
    },
};
</script>
